import { API_TARGET_URL } from '@/lib/data-source/base-url';
import { getProviderDomain, getProviderSlug } from '@/lib/provider-context';
import { categorizeSessionRecoveryError, extractJsonApiErrorCode, extractJsonApiErrorMessage } from '@/lib/jsonapi/errors';
import { logger } from '@/lib/logger';
import { authService } from '@/services/auth.service';
import { AuthResponseData } from '@/types/auth';

interface CustomerSsoGetResponse {
  data?: AuthResponseData;
  redirect_url?: string;
  redirectUrl?: string;
  message?: string;
  error?: string;
  errors?: Array<{
    status?: string;
    code?: string;
    title?: string;
    detail?: string;
  }>;
}

export interface CustomerSsoResult {
  payload: AuthResponseData;
  redirectUrl: string | null;
}

export type CustomerSsoErrorCode = 'expired' | 'consumed' | 'invalid' | 'network' | 'unexpected';

export class CustomerSsoError extends Error {
  code: CustomerSsoErrorCode;
  status?: number;
  cause?: unknown;

  constructor(message: string, code: CustomerSsoErrorCode, status?: number, cause?: unknown) {
    super(message);
    this.name = 'CustomerSsoError';
    this.code = code;
    this.status = status;
    this.cause = cause;
  }
}

class CustomerSsoService {
  private inFlightConsume: Promise<CustomerSsoResult> | null = null;

  private inFlightToken: string | null = null;

  private resolveApiUrl(): string {
    const explicitOrigin = import.meta.env.VITE_PROVIDER_API_ORIGIN;
    if (explicitOrigin && explicitOrigin.length > 0) {
      return explicitOrigin;
    }

    if (API_TARGET_URL && API_TARGET_URL.length > 0) {
      return API_TARGET_URL;
    }

    if (typeof window !== 'undefined' && window.location.origin) {
      return window.location.origin;
    }

    if (import.meta.env.DEV) {
      return 'http://localhost:8880';
    }

    throw new CustomerSsoError('Missing provider API origin for SSO exchange.', 'unexpected');
  }

  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Florentin-Auth-Scope': 'customer',
    };

    const domainOverride =
      typeof import.meta.env.VITE_PROVIDER_DOMAIN_HEADER === 'string'
        ? import.meta.env.VITE_PROVIDER_DOMAIN_HEADER.trim()
        : undefined;
    const domain =
      getProviderDomain() ??
      (domainOverride && domainOverride.length > 0 ? domainOverride : null) ??
      (typeof window !== 'undefined' ? window.location.hostname : null);
    if (domain) {
      headers['X-Provider-Domain'] = domain;
    }

    const slugOverride =
      typeof import.meta.env.VITE_PROVIDER_SLUG_HEADER === 'string'
        ? import.meta.env.VITE_PROVIDER_SLUG_HEADER.trim()
        : undefined;
    const slug = getProviderSlug() ?? (slugOverride && slugOverride.length > 0 ? slugOverride : null);
    if (slug) {
      headers['X-Provider-Slug'] = slug;
    }

    const token = authService.getAuthToken('customer');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  async consume(token: string): Promise<CustomerSsoResult> {
    if (this.inFlightConsume && this.inFlightToken === token) {
      return this.inFlightConsume;
    }

    const run = this.consumeWithRetry(token);
    this.inFlightConsume = run;
    this.inFlightToken = token;

    try {
      return await run;
    } finally {
      if (this.inFlightConsume === run) {
        this.inFlightConsume = null;
        this.inFlightToken = null;
      }
    }
  }

  private async consumeWithRetry(token: string): Promise<CustomerSsoResult> {
    try {
      return await this.consumeOnce(token);
    } catch (error) {
      if (!(error instanceof CustomerSsoError) || error.code !== 'network') {
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, 300));
      return this.consumeOnce(token);
    }
  }

  private async consumeOnce(token: string): Promise<CustomerSsoResult> {
    const origin = this.resolveApiUrl();
    const url = new URL('/api/v1/auth/sso', origin);
    url.searchParams.set('token', token);

    let response: Response;
    try {
      response = await fetch(url.toString(), {
        method: 'POST',
        headers: this.buildHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });
    } catch (error) {
      logger.error('[customer-sso] Network error while exchanging token', error);
      throw new CustomerSsoError('We could not reach the provider. Check your connection and try again.', 'network', undefined, error);
    }

    const body = await this.parseBody(response);

    if (!response.ok) {
      this.handleError(response.status, body);
    }

    const payload = body?.data;

    if (!payload) {
      throw new CustomerSsoError('SSO exchange failed. Missing response payload.', 'unexpected', response.status);
    }

    const redirectUrl =
      body?.redirectUrl ??
      body?.redirect_url ??
      (payload as { redirect_url?: string })?.redirect_url ??
      null;

    return {
      payload,
      redirectUrl: redirectUrl ?? null,
    };
  }

  private async parseBody(response: Response): Promise<CustomerSsoGetResponse | null> {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.toLowerCase().includes('application/json')) {
      return null;
    }

    try {
      return (await response.json()) as CustomerSsoGetResponse;
    } catch {
      return null;
    }
  }

  private handleError(status: number, body: CustomerSsoGetResponse | null): never {
    const errorEnvelope = {
      status,
      payload: body ?? undefined,
    };

    const category = categorizeSessionRecoveryError(errorEnvelope);
    const code = extractJsonApiErrorCode(errorEnvelope)?.toUpperCase();
    const rawMessage = extractJsonApiErrorMessage(errorEnvelope, 'Unable to complete the magic link exchange.');

    if (category === 'EXPIRED') {
      throw new CustomerSsoError('The SSO token has expired.', 'expired', status);
    }

    if (code === 'SSO_TOKEN_ALREADY_USED' || status === 409) {
      throw new CustomerSsoError('The SSO token has already been consumed.', 'consumed', status);
    }

    if (category === 'NOT_FOUND') {
      throw new CustomerSsoError('This sign-in link is invalid or no longer available.', 'invalid', status);
    }

    if (category === 'NETWORK') {
      throw new CustomerSsoError(
        'We could not reach the provider. Check your connection and try again.',
        'network',
        status
      );
    }

    logger.error('[customer-sso] Unexpected API error', { status, body, category, code });
    throw new CustomerSsoError(rawMessage, 'unexpected', status);
  }
}

export const customerSsoService = new CustomerSsoService();
